import type { ActivityChange, ActivityEvent, ChainCheck, ExportKind } from "./api";
import { dateLabel } from "./format";
import { memberName } from "./memberNames";
import type { Saved } from "./save";

/** The CSV files of the cutover (design 26.11 F6), in the order of the Exports page. */
export const EXPORTS: readonly { kind: ExportKind; label: string; text: string }[] = [
  {
    kind: "accounts",
    label: "Accounts",
    text: "One row for each account, with the service plan, the containers and the container pin. The file has no front door position.",
  },
  {
    kind: "pickups",
    label: "Pickups",
    text: "One row for each pickup, with the gallons, the source and if the pickup counts.",
  },
  {
    kind: "pickups-sheet",
    label: "Pickups in the shape of the sheet",
    text: "One row for each account, with a Raw and a Scrap column for each week, the newest week at the left.",
  },
  {
    kind: "parity",
    label: "Parity rows",
    text: "The rows of the Parity page of this cycle, with the sheet value, the app value and the note.",
  },
];

/** The result line of a download that did not finish. */
export function downloadFailure(status: number): Saved {
  if (status === 401) return { ok: false, session: true, text: "Your session ended." };
  if (status === 403 || status === 404) return { ok: false, text: "Your role cannot download this file." };
  if (status >= 500) return { ok: false, text: "The server could not make the file. Try again in a few minutes." };
  return { ok: false, text: "The download did not finish. Check your connection, then try again." };
}

/** Save a file in the browser. */
export function saveBlob(fileName: string, blob: Blob): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  link.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

const SERVICE_TEXT: Record<string, string> = {
  "api:signin": "sign-in",
  "obs-writer": "readings",
  "sensor-rules": "sensor rules",
  "sheet-import": "sheet import",
};

/** The person or the system of an event. A service is the system, with the part that wrote the event. */
export function actorText(event: Pick<ActivityEvent, "actor_type" | "actor_id" | "actor_email" | "actor_name">): string {
  if (event.actor_type === "user") return event.actor_name || event.actor_email || "A removed user";
  if (event.actor_type === "system") return "System";
  const part = SERVICE_TEXT[event.actor_id] ?? event.actor_id.replace(/^api:/, "").replace(/[-_:]+/g, " ").trim();
  return part ? `System (${part})` : "System";
}

/** The source after the action, unless it is the screen or the User column says System already. */
export const sourceNote = (event: Pick<ActivityEvent, "actor_type" | "source">): string =>
  event.source === "screen" || (event.source === "system" && event.actor_type !== "user") ? "" : event.source;

/** Rule U-20: the screens say "container", never "tank". */
const words = (code: string) => code.replace(/_/g, " ").replace(/\btanks?\b/g, (word) => (word === "tank" ? "container" : "containers"));

const capital = (text: string) => text.charAt(0).toUpperCase() + text.slice(1);

const NOUN: Record<string, string> = {
  api_key: "API key",
  container_unit: "Container unit",
  custom_role: "Role",
  follow_up: "Follow-up",
  unit: "Container unit",
};

const VERB: Record<string, string> = {
  accept: "accepted",
  acknowledge: "acknowledged",
  add_pickup: "pickup added",
  choose: "chosen",
  ci_clean: "CI rows removed",
  commercial_status: "contract status changed",
  correct_result: "result corrected",
  customer_acknowledge: "seen by the customer",
  demo_replace: "demo photo replaced",
  fail: "failed",
  fill: "filled",
  forecast_clear: "forecast cleared",
  forecast_reset: "forecast reset",
  import_off: "set to OFF by the import",
  invite_accepted: "invite accepted",
  invite_cancelled: "invite cancelled",
  invite_replaced: "invite replaced",
  invite_sent: "invite sent",
  point: "point moved",
  polygon: "polygon changed",
  preferred_truck: "preferred truck changed",
  result: "result recorded",
  reassign: "reassigned",
  reject: "rejected",
  reopen: "reopened",
  resize: "resized",
  resolve: "resolved",
  restore: "restored",
  revoke: "revoked",
  salespeople: "salespeople changed",
  seed: "added by the seed",
  sheet_change: "changed in the sheet",
  sheet_cleared: "cleared in the sheet",
  sign_in_disable: "sign-in turned off",
  stop_add: "stop added",
  stop_change: "stop changed",
  stop_done: "stop done",
  stop_measured: "stop measured",
  stop_move: "stop moved",
  stop_no_access: "stop with no access",
  stop_partial: "stop partly done",
  stop_remove: "stop removed",
  stop_skip: "stop skipped",
  submit: "submitted",
  swap: "swapped",
  test: "tested",
  version: "new version",
  view: "viewed",
  walk_reset: "walk reset",
  add: "added",
  approve: "approved",
  assign: "assigned",
  cancel: "cancelled",
  change: "changed",
  close: "closed",
  correct: "corrected",
  create: "added",
  deactivate: "deactivated",
  decide: "decided",
  delete: "deleted",
  done: "done",
  download: "downloaded",
  edit: "changed",
  finish: "finished",
  import: "imported",
  invite: "invited",
  lock: "locked",
  make: "made",
  move: "moved",
  note: "note added",
  open: "opened",
  reactivate: "reactivated",
  record: "recorded",
  remove: "removed",
  rename: "renamed",
  request: "requested",
  roles: "roles changed",
  run: "run",
  stage: "stage changed",
  start: "started",
  status: "status changed",
  update: "changed",
  upload: "uploaded",
  use: "use changed",
  win: "won",
};

const ACTION_TEXT: Record<string, string> = {
  "account.bulk_edit": "Accounts changed in bulk",
  "account.bulk_undo": "Bulk change undone",
  "account.door": "Front door moved",
  "account.sales_import": "Account changed by the sales import",
  "account.tank_pin": "Container pin moved",
  "area.pickup_source": "Area pickup source changed",
  "demo.ci_cutover": "CI cutover data added",
  "demo.cutover": "Demo cutover data added",
  "export.download": "File downloaded",
  "import.run_requested": "Import started",
  "import_source.test": "Import access tested",
  "lead.area_owner": "Lead owner set by the area",
  "parity.note": "Parity note added",
  "pickup.duplicate_ack": "Second pickup confirmed",
  "readiness.accept": "Readiness check accepted",
  "setting.change": "Setting changed",
  "signin.link_sent": "Sign-in link sent",
  "signin.link_used": "Sign-in link used",
  "visit.old_check": "Old visit checked",
  "watch.lost_close": "Lost pickup closed",
  "watch.lost_owner": "Lost pickup owner changed",
  "watch.lost_reopen": "Lost pickup opened again",
};

const noun = (type: string) => NOUN[type] ?? capital(words(type));

/** A plain label for an audit action, for example "Container renamed" for tank.rename. */
export function actionText(action: string): string {
  if (ACTION_TEXT[action]) return ACTION_TEXT[action];
  const [type, ...rest] = action.split(".");
  const step = rest.join(".");
  if (!step) return capital(words(type));
  return `${noun(type)} ${VERB[step] ?? words(step)}`;
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const HIDDEN_ID_TYPES = new Set(["member", "tank", "user"]);

/** The record of an event: the label that the server found, else its type, and its id only when the id is not internal. */
export function recordText(event: Pick<ActivityEvent, "resource_type" | "resource_id" | "resource_label">): string {
  if (event.resource_label) return event.resource_type === "area" ? `Area ${event.resource_label}` : event.resource_label;
  const internal = UUID.test(event.resource_id) || HIDDEN_ID_TYPES.has(event.resource_type);
  return internal ? noun(event.resource_type) : `${noun(event.resource_type)} ${words(event.resource_id)}`;
}

const PERCENT_FIELDS = new Set(["share", "tolerance"]);

const isPercent = (field: string) => PERCENT_FIELDS.has(field) || field.endsWith("_share");

/** A share such as 0.11 as "11%". Other values stay as they are. */
export function valueText(field: string, value: string): string {
  if (!value || !isPercent(field) || !/^-?\d+(\.\d+)?$/.test(value)) return value;
  return `${Math.round(Number(value) * 1000) / 10}%`;
}

const HIDDEN_FIELDS = new Set(["updated_at", "created_at"]);
const EMPTY = new Set(["", "[]", "{}", "null"]);
const ISO_TIME = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/;
const CODE = /^[a-z]+(_[a-z]+)+$/;
const isJson = (value: string) => /^[[{]/.test(value.trim()) && !EMPTY.has(value.trim());

const internalField = ({ field, before, after }: ActivityChange) =>
  field === "id" || HIDDEN_FIELDS.has(field) || ((field.endsWith("_id") || field.endsWith("_by")) && [before, after].every((value) => !value || UUID.test(value)));

/** One value of a change: a time in the time zone of the organization, a code in words, a share as a percent. */
export function changeValue(field: string, value: string, when?: (iso: string) => string): string {
  if (EMPTY.has(value.trim())) return "";
  if (when && ISO_TIME.test(value)) return when(value);
  if (CODE.test(value)) return words(value);
  return valueText(field, value);
}

/** The changed fields of an event in one line, for example "Kind: accounts; Tolerance: 11% → 10%".
 * Internal ids, JSON objects and empty changes do not show. */
export function changesText(changes: readonly ActivityChange[] | undefined, when?: (iso: string) => string): string {
  return (changes ?? [])
    .filter((change) => !internalField(change) && !isJson(change.before) && !isJson(change.after))
    .map(({ field, before, after }) => ({ field, old: changeValue(field, before, when), now: changeValue(field, after, when) }))
    .filter(({ old, now }) => old || now)
    .map(({ field, old, now }) => `${capital(words(field))}: ${old && now ? `${old} → ${now}` : now || `${old} → none`}`)
    .join("; ");
}

/** The line of the newest audit chain check, or null before the first check. */
export function chainLine(check: ChainCheck | null | undefined, when: (iso: string) => string): { text: string; ok: boolean } | null {
  if (!check) return null;
  const events = `${check.events.toLocaleString()} ${check.events === 1 ? "event" : "events"}`;
  if (check.ok) return { ok: true, text: `Chain check of ${when(check.checked_at)}: OK, ${events}.` };
  const found = check.problems === 1 ? "1 problem" : `${check.problems} problems`;
  const detail = check.detail.length ? ` ${check.detail.join("; ")}.` : "";
  return { ok: false, text: `Chain check of ${when(check.checked_at)}: ${found} in ${events}.${detail} Tell the developers.` };
}

const LOST_KIND: Record<string, string> = { due: "due, no visit", late: "no pickup for its interval and a week" };
const LOST_REASON: Record<string, string> = {
  visited_off_app: "visited, not recorded in the app",
  not_needed: "no visit was necessary",
  closed: "the account is closed",
  no_access: "no access to the containers",
  other: "other",
};

/** A lost pickup event in words: "Owner: Ann; Week of Oct 12; Why: due, no visit". */
export function lostChangeText(changes: readonly ActivityChange[] | undefined): string {
  const value = (field: string) => (changes ?? []).find((change) => change.field === field)?.after ?? "";
  const was = (changes ?? []).find((change) => change.field === "reason")?.before ?? "";
  const owner = value("owner");
  const due = value("due_on");
  const kind = value("kind");
  const reason = value("reason");
  const note = value("note");
  return [
    owner && `Owner: ${owner.includes("@") ? memberName(owner) : owner}`,
    due && `Week of ${dateLabel(due)}`,
    kind && `Why: ${LOST_KIND[kind] ?? words(kind)}`,
    reason && `Reason: ${LOST_REASON[reason] ?? words(reason)}`,
    !reason && was && `Reason before: ${LOST_REASON[was] ?? words(was)}`,
    note && `Note: ${note}`,
  ]
    .filter(Boolean)
    .join("; ");
}

/** The Change cell of an event. */
export const eventChangesText = (action: string, changes: readonly ActivityChange[] | undefined, when?: (iso: string) => string): string =>
  action.startsWith("watch.lost_") ? lostChangeText(changes) : changesText(changes, when);
